import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Linkedin, Twitter, Instagram, ArrowUpRight } from 'lucide-react';

const footerLinks = {
    services: [
        { name: 'GMB Optimization', path: '/gmb-optimization-services' },
        { name: 'GEO SEO', path: '/geo-seo-services' },
        { name: 'AI Search Optimization', path: '/ai-search-optimization' },
        { name: 'High-Intent SEO', path: '/seo-services' },
        { name: 'Programmatic SEO', path: '/programmatic-seo' },
    ],
    industries: [
        { name: 'Healthcare SEO', path: '/healthcare-seo' },
        { name: 'Clinic SEO', path: '/clinic-seo' },
        { name: 'Travel SEO', path: '/travel-seo' },
        { name: 'Visa Agency SEO', path: '/visa-agency-seo' },
        { name: 'Local Business SEO', path: '/local-business-seo' },
    ],
    company: [
        { name: 'About', path: '/about' },
        { name: 'Case Studies', path: '/case-studies' },
        { name: 'Blog', path: '/blog' },
        { name: 'Contact', path: '/contact' },
    ],
};

export const Footer = () => {
    return (
        <footer className="bg-foreground text-background" data-testid="footer">
            <div className="container-custom">
                {/* Main Footer */}
                <div className="py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
                    {/* Brand Column */}
                    <div className="lg:col-span-2 space-y-6">
                        <Link to="/" className="inline-block">
                            <span className="text-2xl md:text-3xl font-bold font-['Outfit'] text-background">
                                Aitrixy
                            </span>
                        </Link>
                        <p className="text-background/70 text-base max-w-sm leading-relaxed">
                            The Search Visibility & AI Discovery Agency. We help businesses dominate 
                            Google, Maps, and AI search platforms.
                        </p>
                        <div className="space-y-3 text-sm">
                            <a 
                                href="mailto:hello@aitrixy.com" 
                                className="flex items-center gap-3 text-background/70 hover:text-background transition-colors"
                            >
                                <Mail className="w-4 h-4" />
                                hello@aitrixy.com
                            </a>
                            <a 
                                href="tel:+919876543210" 
                                className="flex items-center gap-3 text-background/70 hover:text-background transition-colors"
                            >
                                <Phone className="w-4 h-4" />
                                +91 98765 43210
                            </a>
                            <div className="flex items-start gap-3 text-background/70">
                                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <span>New Delhi, India</span>
                            </div>
                        </div>
                    </div>

                    {/* Services */}
                    <div>
                        <h4 className="text-sm font-semibold uppercase tracking-wider mb-6 text-background">
                            Services
                        </h4>
                        <ul className="space-y-3">
                            {footerLinks.services.map((link) => (
                                <li key={link.path}>
                                    <Link 
                                        to={link.path}
                                        className="text-sm text-background/70 hover:text-background transition-colors inline-flex items-center gap-1 group"
                                    >
                                        {link.name}
                                        <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Industries */}
                    <div>
                        <h4 className="text-sm font-semibold uppercase tracking-wider mb-6 text-background">
                            Industries
                        </h4>
                        <ul className="space-y-3">
                            {footerLinks.industries.map((link) => (
                                <li key={link.path}>
                                    <Link 
                                        to={link.path}
                                        className="text-sm text-background/70 hover:text-background transition-colors inline-flex items-center gap-1 group"
                                    >
                                        {link.name}
                                        <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Company */}
                    <div>
                        <h4 className="text-sm font-semibold uppercase tracking-wider mb-6 text-background">
                            Company
                        </h4>
                        <ul className="space-y-3">
                            {footerLinks.company.map((link) => (
                                <li key={link.path}>
                                    <Link 
                                        to={link.path}
                                        className="text-sm text-background/70 hover:text-background transition-colors inline-flex items-center gap-1 group"
                                    >
                                        {link.name}
                                        <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="py-6 border-t border-background/10 flex flex-col md:flex-row items-center justify-between gap-4">
                    <p className="text-sm text-background/60">
                        © {new Date().getFullYear()} Aitrixy. All rights reserved.
                    </p>
                    <div className="flex items-center gap-4">
                        <a 
                            href="https://linkedin.com" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg text-background/60 hover:text-background hover:bg-background/10 transition-colors"
                            aria-label="LinkedIn"
                        >
                            <Linkedin className="w-5 h-5" />
                        </a>
                        <a 
                            href="https://twitter.com" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg text-background/60 hover:text-background hover:bg-background/10 transition-colors"
                            aria-label="Twitter"
                        >
                            <Twitter className="w-5 h-5" />
                        </a>
                        <a 
                            href="https://instagram.com" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg text-background/60 hover:text-background hover:bg-background/10 transition-colors"
                            aria-label="Instagram"
                        >
                            <Instagram className="w-5 h-5" />
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};
